<template>
    <hts-modal
        :show="visible"
        :title="'Cập nhật trạng thái booking ' + (bookingData.bookingNumber || '')"
        @hide="close">
        <div
            v-if="isLoading"
            class="flex justify-center py-8">
            <hts-loading />
        </div>
        <div
            v-else
            class="p-4">
            <div class="mb-6">
                <p class="text-gray-700 mb-2">
                    Booking:
                    <span class="font-semibold">
                        {{ bookingData.bookingNumber }}
                    </span>
                </p>
                <p class="text-gray-700 mb-2">
                    Từ
                    <span class="font-semibold">
                        {{ bookingData.originName }}
                    </span>
                    đến
                    <span class="font-semibold">
                        {{ bookingData.destinationName }}
                    </span>
                </p>
                <div class="mt-3">
                    <span class="text-sm font-medium">
                        Trạng thái hiện tại:
                    </span>
                    <span
                        class="text-xs font-semibold inline-block py-1 px-2 rounded"
                        :class="getStatusClass(bookingData.status)">
                        {{ getStatusLabel(bookingData.status) }}
                    </span>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-4">
                <hts-form-select
                    v-model="newStatus"
                    :items="availableStatuses"
                    label="Trạng thái mới"
                    placeholder="Chọn trạng thái mới"
                    required />

                <!--
                  <hts-form-textarea
                  v-model="statusNote"
                  label="Ghi chú"
                  placeholder="Nhập ghi chú (nếu có)" />
                -->
            </div>
        </div>

        <template #custom>
            <div class="flex justify-end space-x-3">
                <hts-form-button
                    color="gray"
                    @click="close">
                    {{ $t('labels.cancel') }}
                </hts-form-button>
                <hts-form-button
                    color="main"
                    :loading="isSubmitting"
                    :disabled="!newStatus || newStatus === bookingData.status"
                    @click="handleUpdateStatus">
                    Cập nhật
                </hts-form-button>
            </div>
        </template>
    </hts-modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

const visible = ref(false)
const isLoading = ref(false)
const isSubmitting = ref(false)
const bookingData = ref<any>({})
const newStatus = ref<number | null>(null)
const statusNote = ref('')

// Booking status constants
const BOOKING_STATUS = {
  CANCELLED: -1,
  REJECTED_BY_CARRIER: 0,
  WAITING_CONFIRMATION: 1,
  CONFIRMED: 2,
  IN_PROGRESS: 3,
  COMPLETED: 4
}

// Get available status options based on current status
const availableStatuses = computed(() => {
  const currentStatus = bookingData.value?.status

  if (currentStatus === BOOKING_STATUS.WAITING_CONFIRMATION) {
    return [{ label: 'Đã xác nhận', value: BOOKING_STATUS.CONFIRMED }]
  } else if (currentStatus === BOOKING_STATUS.CONFIRMED) {
    return [{ label: 'Đang vận chuyển', value: BOOKING_STATUS.IN_PROGRESS }]
  } else if (currentStatus === BOOKING_STATUS.IN_PROGRESS) {
    return [{ label: 'Hoàn thành', value: BOOKING_STATUS.COMPLETED }]
  }

  return []
})

const getStatusLabel = (status: number) => {
  switch (status) {
  case BOOKING_STATUS.CANCELLED:
    return 'Đã hủy'
  case BOOKING_STATUS.REJECTED_BY_CARRIER:
    return 'Nhà xe từ chối'
  case BOOKING_STATUS.WAITING_CONFIRMATION:
    return 'Chờ xác nhận'
  case BOOKING_STATUS.CONFIRMED:
    return 'Đã xác nhận'
  case BOOKING_STATUS.IN_PROGRESS:
    return 'Đang vận chuyển'
  case BOOKING_STATUS.COMPLETED:
    return 'Hoàn thành'
  default:
    return 'Không xác định'
  }
}

const getStatusClass = (status: number) => {
  switch (status) {
  case BOOKING_STATUS.CANCELLED:
    return 'bg-error-100 text-error-800'
  case BOOKING_STATUS.REJECTED_BY_CARRIER:
    return 'bg-orange-100 text-orange-800'
  case BOOKING_STATUS.WAITING_CONFIRMATION:
    return 'bg-gray-100 text-gray-800'
  case BOOKING_STATUS.CONFIRMED:
    return 'bg-info-100 text-info-800'
  case BOOKING_STATUS.IN_PROGRESS:
    return 'bg-warning-100 text-warning-800'
  case BOOKING_STATUS.COMPLETED:
    return 'bg-success-100 text-success-800'
  default:
    return 'bg-gray-100 text-gray-800'
  }
}

const open = (data: any) => {
  bookingData.value = data
  newStatus.value = null
  statusNote.value = ''
  visible.value = true
}

const close = () => {
  visible.value = false
}

const handleUpdateStatus = async () => {
  if (!newStatus.value) {
    process.client && $toast().error('Vui lòng chọn trạng thái mới')
    return
  }

  isSubmitting.value = true

  const api = {
    url: '/marketplace/booking/update-status',
    method: 'post'
  }

  try {
    // API để cập nhật trạng thái booking
    const response = await $api(api, {
      body: {
        id: bookingData.value._id,
        status: newStatus.value
      }
    })

    const resData = response?.data?.value

    if (resData?.success) {
      process.client && $toast().success('Cập nhật trạng thái thành công')
      close()
      emits('refresh-table')
    }
    isSubmitting.value = false
  } catch (error) {
    isSubmitting.value = false
  }
}

const emits = defineEmits(['refresh-table'])

defineExpose({
  open,
  close
})
</script>
